import React, { useState, useEffect } from "react";

import {
    Box,
    Divider,
    Grid,
    Typography,
    Button,
    styled,
    Card,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Chip
} from '@mui/material';

import QRCode from "qrcode.react";

import { useNavigate, useParams } from "react-router-dom";

import { useDocuments, documentTypes as deliveryOrderDocType, targetTypes } from "../../../hooks/api/useDocuments";
import { useSystemPrompt } from "../../../hooks/useSystemPrompt";
import { useSystemUtils } from "../../../hooks/useSystemUtils";
import { useAppContextFeatures } from "../../../context/AppContext";
import { useSystemRoutes, routeTypes } from "../../../hooks/useSystemRoutes";
import { useSystemHead, headTitles } from '../../../hooks/useSystemHead';
import { usePermissions, scopeIds, scopeTypes, functionTypes } from "../../../hooks/usePermissions";
import { useTranslations } from "../../../hooks/useTranslations";

import {
    getQrCodeDataUrl,
    outputTypes as scanDataOutputTypes,
    scopeTypes as scanDataScopeTypes,
    targetTypes as scanDataTargetTypes
} from "../../../hooks/useScanData";

import CommonSectionHeader from "../../common/CommonSectionHeader";
import CommonBreadcumbs from "../../common/CommonBreadcumbs";
import CommonRestricted from "../../common/CommonRestricted";
import ViewDocumentDependency from "../ViewDocumentDependency";

import DocumentViewFooter from "../ViewDocumentFooter"
import DocumentQrCode from "../DocumentQrCode";

import { CommonPdf, renderModes, documentTypes as commonPdfDocumentTypes } from "../../common/CommonPdf";

const StyledCard = styled(Card)(({ theme }) => ({
    padding: theme.spacing(3)
}));

const StyledLabel = styled(Typography)(({ theme }) => ({
    fontSize: '0.9rem',
    fontWeight: 800
}))

const StyledContent = styled(Typography)(({ theme }) => ({
    fontSize: '0.9rem'
}))

const StyledContent2 = styled(Typography)(({ theme }) => ({
    fontSize: '0.8rem',
    mt: 2
}))

const StyledDivider = styled(Divider)(({ theme }) => ({
    mt: 1,
    mb: 0,
    borderStyle: 'dashed'
}))

export default function ViewDeliveryOrder({
    routeType
}) {
    const { t } = useTranslations({ ns: ["common", "@document"] })

    const { id } = useParams();

    const { setSystemLoading, openCustomDrawer } = useAppContextFeatures();

    const { promptMessage } = useSystemPrompt();
    const { get } = useDocuments({ documentType: deliveryOrderDocType.deliveryOrder, targetType: targetTypes.deliveryOrder });
    const { formatCurrencyDisplay, currencyAbb } = useSystemUtils();
    const { getRoute } = useSystemRoutes(routeType)
    const { getHtmlHead } = useSystemHead(routeType)
    const { isPermissionAllow } = usePermissions(routeType, scopeIds.document.id, scopeTypes.document.deliveryOrder.id)

    const navigate = useNavigate();

    const [data, setData] = useState()

    const fetchData = async () => {

        setSystemLoading(true);

        try {

            let response = await get({ id, dataTypesArray: ["dependency", "adjustment"] });
            // console.log('response?.data?.items', response?.data)
            setData(response?.data)

        } catch (error) {
            promptMessage(false, `${error.message}`)
        }

        setSystemLoading(false)

    }

    useEffect(() => {

        fetchData();

    }, [])

    const getBreadcumbActionMenus = () => {

        let props = []

        if (isPermissionAllow({ functionType: functionTypes.pageUpdate.id })) {
            props.push({
                displayText: t("editDocument"),
                href: getRoute(`documents/deliveryOrder/update/${id}`),
                // onClick: () => {
                //     navigate(getRoute(`documents/salesOrder/update/${id}`))
                // }
            })
        }

        if (isPermissionAllow({ functionType: functionTypes.pageView.id })) {

            props.push({
                displayText: t("printPreview"),
                onClick: () => {

                    // adding the qrcode into doc data
                    data.qrCodeData = getQrCodeDataUrl()

                    // console.log('data', data)

                    openCustomDrawer({
                        show: true,
                        anchor: "right",
                        title: `Preview PDF`,
                        PaperProps: {
                            sx: { width: 2 / 3 }
                        },
                        // handleOnClose: () => setPreviewPdfOpen(false),
                        ComponentActions: <></>,
                        ComponentBody:
                            <CommonPdf
                                routeType={routeType}
                                renderMode={renderModes.preview.id}
                                documentType={commonPdfDocumentTypes.deliveryOrder.id}
                                document={{
                                    title: data?.document?.doc_number
                                }}
                                data={data}
                            // customData={{
                            //     items: data
                            // }}
                            />
                    })

                    return true;

                }
            })

        }

        return props

    }

    const RenderTable = () => {

        if (!data) return <></>

        return (
            <TableContainer
                // component={Paper}
                sx={{
                    minHeight: 400,
                    maxHeight: 400
                }}
            >
                <Table
                    size="small"
                    stickyHeader
                >
                    <TableHead>
                        <TableRow>
                            <TableCell width={20}>#</TableCell>
                            {/* <TableCell align="center" width={120}>Stock Posted</TableCell> */}
                            <TableCell width={200}>{t('code')}</TableCell>
                            <TableCell width={250}>{t('itemName')}</TableCell>
                            <TableCell width={400}>{t('description')}</TableCell>
                            {/* <TableCell>Code</TableCell> */}
                            <TableCell align="right">{t('qty')}</TableCell>
                            <TableCell align="right" width={150}>{t('uom')}</TableCell>
                            <TableCell align="right" width={150}>{t('price')}</TableCell>
                            <TableCell align="right" width={150}>{t('total')} ({currencyAbb})</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {
                            data?.items.map((row, index) => (
                                <TableRow key={row.id}>
                                    <TableCell>{(index + 1)}</TableCell>
                                    {/* <TableCell align="center">
                                    {
                                        row.is_post_stock === "1"? 'Yes' : '-'
                                    }
                                </TableCell> */}
                                    <TableCell>{row.code}</TableCell>
                                    <TableCell>{row.item_name}</TableCell>
                                    <TableCell>{row.description}</TableCell>
                                    {/* <TableCell>{row.code}</TableCell> */}
                                    <TableCell align="right">{row.qty}</TableCell>
                                    <TableCell align="right">{row.uom_name}</TableCell>
                                    <TableCell align="right">{row.uom_price}</TableCell>
                                    <TableCell align="right">{(row.sub_price)}</TableCell>
                                    {/* <TableCell align="right">{row.unit}</TableCell>
                                <TableCell align="right">{ccyFormat(row.price)}</TableCell> */}
                                </TableRow>
                            ))
                        }
                    </TableBody>
                </Table>
            </TableContainer>
        )
    }

    const RenderTableFooter = () => {

        if (!data) return <></>

        return (
            <DocumentViewFooter
                data={data}
                showTax={Boolean(true)}
            />
        )
    }

    const RenderFormProfile = () => {
        return (
            <StyledCard>
                <Grid container spacing={0.5}>
                    <Grid item xs={6} sm={6} md={6} textAlign={"left"}>

                        <CommonSectionHeader
                            title={t(`profile`)}
                            subTitle={t(`relatedProfileInfoAttachedToDocument`)}
                        />

                    </Grid>

                    <Grid item xs={6} sm={6} md={6} textAlign={"right"}>

                        <Chip label={data?.document?.user_type} sx={{ textTransform: 'capitalize' }} size="small" variant="outlined" color={'primary'} />

                    </Grid>

                    <Grid item xs={12}>
                        <StyledDivider />
                    </Grid>

                    <Grid item xs={12} sm={12} md={12} sx={{ mt: 2 }}>
                        <Grid container spacing={0.5}>

                            <Grid item xs={12} sm={12} md={4} textAlign={"left"}>
                                <StyledLabel>{t('location')}</StyledLabel>
                            </Grid>

                            <Grid item xs={12} sm={12} md={8} textAlign={"left"}>
                                <StyledContent>
                                    {data?.document?.location_name}
                                </StyledContent>
                                <StyledContent2>
                                    {data?.document?.state_name}
                                </StyledContent2>
                            </Grid>

                            <Grid item xs={12} sm={12} md={4} textAlign={"left"}>
                                <StyledLabel>{t('placeBy')}</StyledLabel>
                            </Grid>
                            <Grid item xs={12} sm={12} md={8} textAlign={"left"}>
                                <StyledContent>
                                    {data?.document?.profile_name} ({data?.document?.code})
                                </StyledContent>
                                <StyledContent2>
                                    {data?.document?.address_1} {data?.document?.address_2} {data?.document?.postcode}, {data?.document?.area} {data?.document?.country_name}
                                </StyledContent2>
                                <StyledContent2>
                                    {data?.document?.contact} / {data?.document?.email}
                                </StyledContent2>
                            </Grid>

                            <Grid item xs={12} sm={12} md={4} textAlign={"left"}>
                                <StyledLabel>{t('billTo')}</StyledLabel>
                            </Grid>
                            <Grid item xs={12} sm={12} md={8} textAlign={"left"}>
                                <StyledContent>
                                    {data?.document?.billing_profile_name} ({data?.document?.billing_code})
                                </StyledContent>
                                {/* <StyledContent>
                                    {data?.document?.billing_address_1} {data?.document?.billing_address_2} {data?.document?.billing_postcode}, 
                                    {data?.document?.billing_area_name} {data?.document?.billing_country_name}
                                </StyledContent> */}
                            </Grid>

                            <Grid item xs={12} sm={12} md={4} textAlign={"left"}>
                                <StyledLabel>{t('shipTo')}</StyledLabel>
                            </Grid>
                            <Grid item xs={12} sm={12} md={8} textAlign={"left"}>
                                <StyledContent>
                                    {data?.document?.delivery_profile_name} ({data?.document?.delivery_code})
                                </StyledContent>
                                {/* <StyledContent>
                                    {data?.document?.delivery_address_1} {data?.document?.delivery_address_2} {data?.document?.delivery_postcode}, 
                                    {data?.document?.delivery_area_name} {data?.document?.delivery_country_name}
                                </StyledContent> */}
                            </Grid>

                        </Grid>
                    </Grid>

                </Grid>
            </StyledCard>
        )
    }

    const RenderFormDocument = () => {
        return (
            <StyledCard>
                <Grid container spacing={0.5}>
                    <Grid item xs={12} sm={12} md={12} textAlign={"left"}>

                        <CommonSectionHeader
                            title={t(`document`)}
                            subTitle={t(`basicInformationAboutTheDocument`)}
                        />

                    </Grid>
                    <Grid item xs={12}>
                        <StyledDivider />
                    </Grid>
                    <Grid item xs={12} sm={12} md={12} sx={{ mt: 2 }}>
                        <Grid container spacing={0.5}>

                            <Grid item xs={12} sm={12} md={4} textAlign={"left"}>
                                <StyledLabel>{t('documentNo')}</StyledLabel>
                            </Grid>
                            <Grid item xs={12} sm={12} md={8} textAlign={"left"}>
                                <StyledContent>
                                    {data?.document.doc_number}
                                </StyledContent>
                            </Grid>

                            <Grid item xs={12} sm={12} md={4} textAlign={"left"}>
                                <StyledLabel>{t('documentDate')}</StyledLabel>
                            </Grid>
                            <Grid item xs={12} sm={12} md={8} textAlign={"left"}>
                                <StyledContent>
                                    {data?.document.doc_date}
                                </StyledContent>
                            </Grid>

                            {/* <Grid item xs={12} sm={12} md={4} textAlign={"left"}>
                                <StyledLabel>
                                     Status
                                </StyledLabel>
                            </Grid>
                            <Grid item xs={12} sm={12} md={8} textAlign={"left"}>
                                <StyledContent>
                                    {data?.document.is_active_status === "1"? 'Active' : 'Cancelled'}
                                </StyledContent>
                            </Grid> */}

                            {/* <Grid item xs={12} sm={12} md={4} textAlign={"left"}>
                                <StyledLabel>
                                     Stock Posted
                                </StyledLabel>
                            </Grid>
                            <Grid item xs={12} sm={12} md={8} textAlign={"left"}>
                                <StyledContent>
                                    {data?.document.is_post_stock === "1"? 'Completed' : 'No'}
                                </StyledContent>
                            </Grid> */}

                            <Grid item xs={12} sm={12} md={4} textAlign={"left"}>
                                <StyledLabel>
                                    {t('remark')}
                                </StyledLabel>
                            </Grid>
                            <Grid item xs={12} sm={12} md={8} textAlign={"left"}>
                                <StyledContent>
                                    {data?.document.doc_remark}
                                </StyledContent>
                            </Grid>

                            {/* <Grid item xs={12} sm={12} md={4} textAlign={"left"}>
                                <StyledLabel>
                                    Tracking No.
                                </StyledLabel>
                            </Grid>
                            <Grid item xs={12} sm={12} md={8} textAlign={"left"}>
                                <StyledContent>
                                    {data?.document.tracking_no}
                                </StyledContent>
                            </Grid> */}

                            {/* <Grid item xs={12} sm={12} md={4} textAlign={"left"}>
                                <StyledLabel>
                                    Billing
                                </StyledLabel>
                            </Grid>
                            <Grid item xs={12} sm={12} md={8} textAlign={"left"}>
                                <StyledContent>
                                    {data?.document.type}
                                </StyledContent>
                            </Grid> */}

                            <Grid item xs={12} sm={12} md={4} textAlign={"left"}>
                                <StyledLabel>
                                    {t('collection')}
                                </StyledLabel>
                            </Grid>
                            <Grid item xs={12} sm={12} md={8} textAlign={"left"}>
                                <StyledContent>
                                    {data?.document.is_payment_collect === "1" ? 'Required' : 'No'} ({data?.document.collection_amount} / {data?.document.require_collect_amount})
                                </StyledContent>
                                <StyledContent>
                                    {data?.collection_remark}
                                </StyledContent>
                            </Grid>

                            <Grid item xs={12} sm={12} md={4} textAlign={"left"}>
                                <StyledLabel>
                                    {t('distributeVia')}
                                </StyledLabel>
                            </Grid>
                            <Grid item xs={12} sm={12} md={8} textAlign={"left"}>
                                <StyledContent>
                                    {data?.document.distribution_location_name ? data?.document.distribution_location_name : '-'}
                                </StyledContent>
                                {/* <StyledContent>
                                    {data?.collection_remark}
                                </StyledContent> */}
                            </Grid>

                        </Grid>
                    </Grid>
                </Grid>
            </StyledCard>
        )
    }

    const RenderFormDependency = () => {
        return (
            <ViewDocumentDependency
                routeType={routeType}
                data={data?.document}
            />
        )
    }

    const RenderForm = () => {
        return (
            <>
                <Grid container spacing={1}>
                    <Grid item xs={12} sm={12} md={5} textAlign={"left"}>
                        {
                            RenderFormDocument()
                        }
                    </Grid>
                    <Grid item xs={12} sm={12} md={7} textAlign={"left"}>
                        {
                            RenderFormProfile()
                        }

                        {
                            RenderFormDependency()
                        }

                        {/* {
                            RenderFormPayment()
                        } */}
                    </Grid>
                </Grid>

                <Box
                    sx={{
                        textAlign: 'left',
                        mt: 3,
                        pl: 0.5,
                    }}
                >
                    <CommonSectionHeader
                        title={`Items`}
                        subTitle={`Detail information about the items involved`}
                    />
                </Box>

                <StyledCard
                    sx={{
                        mt: 1.5,
                        p: 0
                    }}
                >
                    <Grid container rowSpacing={0}>
                        <Grid item md={12}>
                            <RenderTable />
                        </Grid>
                        <Grid item md={12}>
                            <RenderTableFooter />
                        </Grid>

                    </Grid>
                </StyledCard>
                <Grid container spacing={1} sx={{ pl: 2, pr: 2 }}>
                    <Grid item md={12} textAlign={"center"}>
                        <Button
                            type="button"
                            variant="contained"
                            sx={{
                                mt: 3,
                                mb: 2
                            }}
                            // loading={isLoading}
                            onClick={() => navigate(-1)}
                        >
                            Back
                        </Button>
                    </Grid>
                </Grid>
            </>
        )
    }

    return (
        <Box>
            {
                getHtmlHead({ routeType, title: headTitles.viewDeliveryOrder })
            }

            <CommonBreadcumbs
                routeType={routeType}
                pageTitle={`${t('deliveryOrders')} ${data?.document.doc_number}`}
                actionMenus={
                    getBreadcumbActionMenus()
                }
                breadcrumbs={[
                    {
                        displayText: t('deliveryOrders'),
                        props: {
                            href: getRoute('documents/deliveryOrders')
                        }
                    },
                    {
                        displayText: `#${data?.document.doc_number}`,
                        props: {
                            color: 'secondary.main',
                        }
                    },
                ]}
            />

            {
                isPermissionAllow({ functionType: functionTypes.pageView.id }) ?
                    RenderForm()
                    :
                    (
                        <CommonRestricted />
                    )
            }

            {
                DocumentQrCode({
                    data,
                    outputType: scanDataOutputTypes.qrcode,
                    scopeType: scanDataScopeTypes.document,
                    targetType: scanDataTargetTypes.document.cashSales
                })
            }

        </Box>
    )
}